/**
 * Premium Hover Animation System
 * CraftCloudIndia™ / Candleroma
 *
 * Handles JS-driven presets only:
 *   - Premium Parallax  (mouse-position tilt via CSS vars)
 *   - Magnetic Hover    (cursor-attraction translate via CSS vars)
 *
 * Pure CSS presets (luxury-lift, editorial-zoom, champagne-glow, reveal-border)
 * require no JavaScript — they are handled entirely in premium-hover.css.
 *
 * Performance notes:
 *   - All DOM mutations go through CSS custom properties on the element itself
 *   - requestAnimationFrame throttle for mousemove
 *   - IntersectionObserver to activate only visible cards
 *   - ResizeObserver to update cached bounding rects
 */

(function () {
  'use strict';

  // ─── Guards ────────────────────────────────────────────────────────────────

  const body = document.body;

  function getPreset() {
    return body.dataset.phxPreset || 'none';
  }

  function isMobileEnabled() {
    return body.classList.contains('phx-mobile-enabled');
  }

  function isReducedMotion() {
    return (
      body.classList.contains('phx-respect-reduced-motion') &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // Only touch/pointer-coarse = mobile/tablet
  function isTouchDevice() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  function shouldActivate() {
    if (isReducedMotion()) return false;
    if (isTouchDevice() && !isMobileEnabled()) return false;
    const preset = getPreset();
    return preset === 'premium-parallax' || preset === 'magnetic';
  }

  // ─── Shared utilities ──────────────────────────────────────────────────────

  const PHX_TARGET = '.phx-target';

  /** Clamp a number between min and max */
  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  /**
   * Read --phx-parallax-max / --phx-magnetic-max from the computed body style.
   * Returns a pixel value (number).
   */
  function getMaxOffset(varName) {
    const raw = getComputedStyle(body).getPropertyValue(varName).trim();
    return parseFloat(raw) || 6;
  }

  /** RAF-throttled mousemove handler factory */
  function rafThrottle(fn) {
    let rafId = null;
    return function (...args) {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        fn.apply(this, args);
        rafId = null;
      });
    };
  }

  // ─── Bounding rect cache (refresh on resize) ───────────────────────────────

  const rectCache = new WeakMap();

  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      rectCache.delete(entry.target);
    });
  });

  function getRect(el) {
    if (!rectCache.has(el)) {
      rectCache.set(el, el.getBoundingClientRect());
    }
    return rectCache.get(el);
  }

  // Invalidate all cached rects on scroll (viewport positions change)
  window.addEventListener('scroll', () => rectCache.clear(), { passive: true });

  // ─── PARALLAX PRESET ───────────────────────────────────────────────────────

  function initParallax(targets) {
    const MAX_DEG = 6; // Maximum tilt in degrees

    targets.forEach((el) => {
      resizeObserver.observe(el);

      const onMove = rafThrottle((e) => {
        const rect = el.getBoundingClientRect();
        rectCache.set(el, rect); // refresh on actual move

        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        const dx = (e.clientX - cx) / (rect.width / 2);
        const dy = (e.clientY - cy) / (rect.height / 2);

        const ry = clamp(dx * MAX_DEG, -MAX_DEG, MAX_DEG);
        const rx = clamp(-dy * MAX_DEG, -MAX_DEG, MAX_DEG);

        el.classList.add('phx-moving');
        el.style.setProperty('--phx-rx', `${rx}deg`);
        el.style.setProperty('--phx-ry', `${ry}deg`);
      });

      const onLeave = () => {
        el.classList.remove('phx-moving');
        el.classList.add('phx-resetting');
        el.style.setProperty('--phx-rx', '0deg');
        el.style.setProperty('--phx-ry', '0deg');

        const onTransitionEnd = () => {
          el.classList.remove('phx-resetting');
          el.removeEventListener('transitionend', onTransitionEnd);
        };
        el.addEventListener('transitionend', onTransitionEnd, { once: true });
      };

      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });
  }

  // ─── MAGNETIC PRESET ───────────────────────────────────────────────────────

  function initMagnetic(targets) {
    targets.forEach((el) => {
      resizeObserver.observe(el);

      const onMove = rafThrottle((e) => {
        const rect = el.getBoundingClientRect();
        rectCache.set(el, rect);

        const maxOffset = getMaxOffset('--phx-magnetic-max');
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        // Attraction strength: closer to center = stronger pull
        const dx = (e.clientX - cx) / (rect.width / 2);
        const dy = (e.clientY - cy) / (rect.height / 2);

        const tx = clamp(dx * maxOffset * 0.4, -maxOffset, maxOffset);
        const ty = clamp(dy * maxOffset * 0.4, -maxOffset, maxOffset);

        el.classList.add('phx-moving');
        el.style.setProperty('--phx-tx', `${tx}px`);
        el.style.setProperty('--phx-ty', `${ty}px`);
      });

      const onLeave = () => {
        el.classList.remove('phx-moving');
        el.classList.add('phx-resetting');
        el.style.setProperty('--phx-tx', '0px');
        el.style.setProperty('--phx-ty', '0px');

        el.addEventListener(
          'transitionend',
          () => el.classList.remove('phx-resetting'),
          { once: true }
        );
      };

      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });
  }

  // ─── Intersection observer — only bind to visible targets ─────────────────

  function bindInteractiveTargets(initFn) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            initFn([entry.target]);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px' }
    );

    document.querySelectorAll(PHX_TARGET).forEach((el) => io.observe(el));

    // Also watch for dynamically rendered targets (section re-renders in editor)
    if (window.MutationObserver) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            const targets = node.matches?.(PHX_TARGET)
              ? [node]
              : [...node.querySelectorAll(PHX_TARGET)];
            targets.forEach((t) => io.observe(t));
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // ─── Bootstrap ─────────────────────────────────────────────────────────────

  function init() {
    if (!shouldActivate()) return;

    const preset = getPreset();

    if (preset === 'premium-parallax') {
      bindInteractiveTargets(initParallax);
    } else if (preset === 'magnetic') {
      bindInteractiveTargets(initMagnetic);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section re-renders in theme editor
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(init, 100);
    });
  }
})();
